/**
 * Verify_Email_Screen.tsx — SK Platform
 * Lands from the signup verification email (?mode=verify&token=…, captured in
 * App.tsx). Verifies the token via verifyEmail() — which activates the org —
 * then redirects to login. Endpoint resolved only by Api_Endpoints.ts.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Platform,
  ActivityIndicator,
} from 'react-native';

import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { AuthStackParamList } from '../../navigation/types';
import { CONTENT } from '../../utils/responsive';
import { clearAuthDeepLink } from '../../navigation/linking';

import PrimaryButton from '../../components/primaryButton';
import { verifyEmail } from '../../services/auth/auth';
import { getApiErrorMessage } from '../../utils/errorHandler';
import { useTheme } from '../../context/themeContext';
import { notifyEmailVerified } from '../../navigation/authChannel';
import AuthBrandHeader from '../../components/authBrandHeader';

type Status = 'verifying' | 'success' | 'error';

// On success we hold a short, friendly countdown before sending the user to
// the sign-in screen, cycling encouraging lines so the wait feels intentional.
const COUNTDOWN_FROM = 5;
const COUNTDOWN_LINES = [
  'Your email is verified 🎉',
  'Your organization is now active.',
  'Everything is set up for you.',
  'Polishing the final details…',
  'Taking you to sign in…',
];

const Verify_Email_Screen: React.FC = () => {
  const { theme } = useTheme();
  const { colors } = theme;
  const navigation = useNavigation<NativeStackNavigationProp<AuthStackParamList, 'verifyEmail'>>();
  const { params } = useRoute<RouteProp<AuthStackParamList, 'verifyEmail'>>();
  const token = params?.token ?? '';

  // Done → drop the ?mode/?token query, then send the user to the sign-in
  // screen. Signing in routes them onward (Step 2 if their profile is still
  // pending, else the dashboard). Memoized so the countdown timer doesn't
  // reset on unrelated re-renders.
  const onDone = useCallback(() => {
    clearAuthDeepLink();
    navigation.navigate('login');
  }, [navigation]);

  const [status, setStatus] = useState<Status>(token ? 'verifying' : 'error');
  const [errorMsg, setErrorMsg] = useState(
    token ? '' : 'This verification link is missing or invalid.',
  );
  const [seconds, setSeconds] = useState(COUNTDOWN_FROM);

  // Verify the token once on mount.
  useEffect(() => {
    if (!token) return;
    let active = true;
    verifyEmail(token)
      .then((res) => {
        if (!active) return;
        // Tell the original "check your email" tab to advance to login.
        notifyEmailVerified(res?.data?.user?.email);
        setStatus('success');
      })
      .catch((err) => {
        if (!active) return;
        setErrorMsg(getApiErrorMessage(err, 'Verification failed. The link may have expired.'));
        setStatus('error');
      });
    return () => { active = false; };
  }, [token]);

  // On success, tick a 1s countdown; redirect to login when it reaches zero.
  useEffect(() => {
    if (status !== 'success') return;
    if (seconds <= 0) { onDone?.(); return; }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [status, seconds, onDone]);

  // Which encouraging line to show for the current second (last line at 0).
  const countdownLine = COUNTDOWN_LINES[Math.min(
    COUNTDOWN_LINES.length - 1,
    COUNTDOWN_FROM - seconds,
  )];

  return (
    <View style={[styles.root, { backgroundColor: colors.paper }]}>
      <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
        <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
          <AuthBrandHeader />

          {status === 'verifying' && (
            <>
              <Text style={[styles.title, { color: colors.accent }]}>Verifying your email…</Text>
              <View style={{ marginTop: 18, marginBottom: 8 }}>
                <ActivityIndicator size="large" color={colors.accent} />
              </View>
              <Text style={[styles.sub, { color: colors.ink2 }]}>Hang tight — this only takes a moment.</Text>
            </>
          )}

          {status === 'success' && (
            <>
              <View style={[styles.okCircle, { backgroundColor: colors.successSoft, borderColor: colors.successLine }]}><Text style={[styles.okIcon, { color: colors.success }]}>✓</Text></View>
              <Text style={[styles.title, { color: colors.accent }]}>Email verified</Text>
              <Text style={[styles.sub, { color: colors.ink2 }]}>{countdownLine}</Text>

              <View style={styles.countdownRow}>
                <View style={[styles.countdownBadge, { backgroundColor: colors.accentSoft, borderColor: colors.accentLine }]}>
                  <Text style={[styles.countdownNum, { color: colors.accent }]}>{seconds}</Text>
                </View>
                <Text style={[styles.countdownHint, { color: colors.ink4 }]}>
                  Redirecting to sign in in {seconds} second{seconds === 1 ? '' : 's'}…
                </Text>
              </View>

              <PrimaryButton title="Continue to login now" onPress={() => onDone?.()} style={styles.btn} />
            </>
          )}

          {status === 'error' && (
            <>
              <View style={[styles.errCircle, { backgroundColor: colors.dangerSoft, borderColor: colors.dangerLine }]}><Text style={[styles.errIcon, { color: colors.danger }]}>!</Text></View>
              <Text style={[styles.title, { color: colors.accent }]}>Verification failed</Text>
              <View style={[styles.errorBox, { backgroundColor: colors.dangerSoft, borderColor: colors.dangerLine }]}>
                <Text style={[styles.errorText, { color: colors.danger }]}>{errorMsg}</Text>
              </View>
              <PrimaryButton title="Back to login" onPress={() => onDone?.()} style={styles.btn} />
            </>
          )}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    ...(Platform.OS === 'web' ? ({ minHeight: '100vh' } as any) : {}),
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 16,
  },
  card: {
    width: '100%',
    maxWidth: CONTENT.form,
    borderRadius: 24,
    padding: 32,
    borderWidth: 1,
    alignItems: 'center',
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 8px 40px rgba(15,23,42,0.10)' } as any)
      : {
          shadowColor: '#0F172A',
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.1,
          shadowRadius: 24,
          elevation: 8,
        }),
  },
  title: {
    fontSize: 26,
    fontWeight: '900',
    marginBottom: 8,
    textAlign: 'center',
    letterSpacing: -0.8,
    lineHeight: 34,
  },
  sub: {
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 8,
  },
  okCircle: {
    width: 56, height: 56, borderRadius: 28,
    borderWidth: 1, alignItems: 'center', justifyContent: 'center', marginBottom: 16,
  },
  okIcon: { fontSize: 28, fontWeight: '900', includeFontPadding: false },
  countdownRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginTop: 6,
    marginBottom: 4,
  },
  countdownBadge: {
    width: 34, height: 34, borderRadius: 17,
    borderWidth: 1,
    alignItems: 'center', justifyContent: 'center',
  },
  countdownNum: { fontSize: 16, fontWeight: '900', includeFontPadding: false },
  countdownHint: { fontSize: 12, fontWeight: '600' },
  errCircle: {
    width: 56, height: 56, borderRadius: 28,
    borderWidth: 1, alignItems: 'center', justifyContent: 'center', marginBottom: 16,
  },
  errIcon: { fontSize: 28, fontWeight: '900', includeFontPadding: false },
  errorBox: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 14,
    marginTop: 4,
    marginBottom: 8,
    width: '100%',
  },
  errorText: { fontSize: 13, lineHeight: 20, fontWeight: '500', textAlign: 'center' },
  btn: { marginTop: 16, width: '100%' },
});

export default Verify_Email_Screen;
